import { Vector2 } from '@shared/types';

export class Camera {
  public position: Vector2 = { x: 0, y: 0 };
  public zoom: number = 1;

  private target: Vector2 | null = null;
  private screenWidth: number;
  private screenHeight: number;

  // Smooth follow settings
  private followSpeed: number = 5;
  private deadZone: number = 50; // pixels before camera starts following

  // Screen shake offset
  private _shakeOffsetX: number = 0;
  private _shakeOffsetY: number = 0;

  get shakeOffsetX(): number { return this._shakeOffsetX; }
  get shakeOffsetY(): number { return this._shakeOffsetY; }

  // Zoom animation (lerp-based)
  private targetZoom: number = 1;
  private zoomAnimating: boolean = false;
  private zoomDuration: number = 0;
  private zoomTimer: number = 0;
  private zoomStart: number = 1;
  private baseZoom: number = 1;

  // Deferred zoom-back (replaces setTimeout for GC safety)
  private pendingZoomBack: { delay: number; timer: number; target: number; duration: number } | null = null;

  constructor(screenWidth: number, screenHeight: number) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
  }

  setScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
  }

  setTarget(target: Vector2): void {
    this.target = target;
  }

  clearTarget(): void {
    this.target = null;
  }

  setPosition(x: number, y: number): void {
    this.position.x = x;
    this.position.y = y;
  }

  /** Returns the camera position directly. Do NOT mutate the returned object. */
  getPosition(): Vector2 {
    return this.position;
  }

  update(deltaTime: number): void {
    if (!this.target) return;

    // Calculate desired camera position (center on target)
    const desiredX = this.target.x - this.screenWidth / 2 / this.zoom;
    const desiredY = this.target.y - this.screenHeight / 2 / this.zoom;

    // Smooth interpolation (use squared distance to avoid sqrt)
    const dx = desiredX - this.position.x;
    const dy = desiredY - this.position.y;

    const distSq = dx * dx + dy * dy;

    if (distSq > this.deadZone * this.deadZone) {
      this.position.x += dx * this.followSpeed * deltaTime;
      this.position.y += dy * this.followSpeed * deltaTime;
    }

    // Zoom animation (smooth lerp)
    if (this.zoomAnimating) {
      this.zoomTimer += deltaTime;
      if (this.zoomTimer >= this.zoomDuration) {
        this.zoom = this.targetZoom;
        this.zoomAnimating = false;
      } else {
        const t = this.zoomTimer / this.zoomDuration;
        // Smooth ease-out interpolation: 1 - (1-t)^2
        const eased = 1 - (1 - t) * (1 - t);
        this.zoom = this.zoomStart + (this.targetZoom - this.zoomStart) * eased;
      }
    }

    // Deferred zoom-back (replaces setTimeout — no GC leak)
    if (this.pendingZoomBack) {
      this.pendingZoomBack.timer += deltaTime;
      if (this.pendingZoomBack.timer >= this.pendingZoomBack.delay) {
        this.zoomTo(this.pendingZoomBack.target, this.pendingZoomBack.duration);
        this.pendingZoomBack = null;
      }
    }
  }

  // Set screen shake offset (called by ScreenShake system)
  setShakeOffset(offsetX: number, offsetY: number): void {
    this._shakeOffsetX = offsetX;
    this._shakeOffsetY = offsetY;
  }

  // Convert world coordinates to screen coordinates
  worldToScreen(worldX: number, worldY: number): Vector2 {
    return {
      x: (worldX - this.position.x) * this.zoom + this._shakeOffsetX,
      y: (worldY - this.position.y) * this.zoom + this._shakeOffsetY,
    };
  }

  // Allocation-free variant: writes result into a reusable scratch vector
  private static _scratch: Vector2 = { x: 0, y: 0 };
  worldToScreenInPlace(worldX: number, worldY: number): Vector2 {
    const s = Camera._scratch;
    s.x = (worldX - this.position.x) * this.zoom + this._shakeOffsetX;
    s.y = (worldY - this.position.y) * this.zoom + this._shakeOffsetY;
    return s;
  }

  // Convert screen coordinates to world coordinates
  screenToWorld(screenX: number, screenY: number): Vector2 {
    return {
      x: screenX / this.zoom + this.position.x,
      y: screenY / this.zoom + this.position.y,
    };
  }

  /**
   * Check if a world-space point is within the camera viewport.
   * Convenience alias with a configurable margin (defaults to 100px).
   * Use this for frustum culling before rendering or updating off-screen entities.
   */
  isInView(worldX: number, worldY: number, margin: number = 100): boolean {
    const sx = (worldX - this.position.x) * this.zoom + this._shakeOffsetX;
    const sy = (worldY - this.position.y) * this.zoom + this._shakeOffsetY;
    return (
      sx >= -margin &&
      sx <= this.screenWidth + margin &&
      sy >= -margin &&
      sy <= this.screenHeight + margin
    );
  }

  // Check if a point is visible on screen (inlined to avoid Vector2 allocation)
  isVisible(worldX: number, worldY: number, margin: number = 100): boolean {
    const sx = (worldX - this.position.x) * this.zoom + this._shakeOffsetX;
    const sy = (worldY - this.position.y) * this.zoom + this._shakeOffsetY;
    return (
      sx >= -margin &&
      sx <= this.screenWidth + margin &&
      sy >= -margin &&
      sy <= this.screenHeight + margin
    );
  }

  // Check if a rectangle is visible on screen (inlined to avoid Vector2 allocation)
  isRectVisible(
    worldX: number,
    worldY: number,
    width: number,
    height: number,
    margin: number = 100
  ): boolean {
    const sx = (worldX - this.position.x) * this.zoom + this._shakeOffsetX;
    const sy = (worldY - this.position.y) * this.zoom + this._shakeOffsetY;
    const scaledWidth = width * this.zoom;
    const scaledHeight = height * this.zoom;

    return (
      sx + scaledWidth >= -margin &&
      sx <= this.screenWidth + margin &&
      sy + scaledHeight >= -margin &&
      sy <= this.screenHeight + margin
    );
  }

  // Get visible world bounds (inlined to avoid Vector2 allocations)
  private _visibleBounds = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  getVisibleBounds(): { minX: number; minY: number; maxX: number; maxY: number } {
    const invZoom = 1 / this.zoom;
    this._visibleBounds.minX = this.position.x;
    this._visibleBounds.minY = this.position.y;
    this._visibleBounds.maxX = this.screenWidth * invZoom + this.position.x;
    this._visibleBounds.maxY = this.screenHeight * invZoom + this.position.y;
    return this._visibleBounds;
  }

  setZoom(zoom: number): void {
    this.zoom = Math.max(0.5, Math.min(2, zoom));
  }

  zoomIn(amount: number = 0.1): void {
    this.setZoom(this.zoom + amount);
  }

  zoomOut(amount: number = 0.1): void {
    this.setZoom(this.zoom - amount);
  }

  zoomTo(target: number, duration: number): void {
    this.targetZoom = Math.max(0.5, Math.min(2, target));
    this.zoomStart = this.zoom;
    this.zoomDuration = Math.max(0.01, duration);
    this.zoomTimer = 0;
    this.zoomAnimating = true;
  }

  zoomPulse(amount: number, duration: number): void {
    // Quick zoom in and back - store current zoom as base
    this.baseZoom = this.zoom;
    this.zoomTo(this.zoom * amount, duration / 2);
    this.pendingZoomBack = { delay: duration / 2, timer: 0, target: this.baseZoom, duration: duration / 2 };
  }

  // Boss encounter: 0.5s zoom 1.0x -> 1.1x -> back to 1.0x
  bossEncounterZoom(): void {
    this.baseZoom = this.zoom;
    this.zoomTo(1.1, 0.25);
    this.pendingZoomBack = { delay: 0.25, timer: 0, target: this.baseZoom, duration: 0.25 };
  }

  // Player death: 0.8s zoom out to 0.85x (stays zoomed out)
  playerDeathZoom(): void {
    this.zoomTo(0.85, 0.8);
  }

  // Critical hit: quick 0.15s zoom 1.02x pulse and back
  criticalHitPulse(): void {
    this.baseZoom = this.zoom;
    this.zoomTo(this.zoom * 1.02, 0.075);
    this.pendingZoomBack = { delay: 0.075, timer: 0, target: this.baseZoom, duration: 0.075 };
  }
}
